import { useEffect, useRef } from 'react'
import { useLocation, useNavigate, useNavigationType } from 'react-router-dom'

/**
 * Back in the Session Builder, the same way every time (Charles,
 * 2026-10-07: Back "sometimes seems to loop around to what previous
 * screens were"). The cause was that the Back links OPENED their target as
 * a new screen, so the history grew main → booking → main → booking … and
 * the phone's own back chevron then walked round that circle.
 *
 * Now each Session Builder screen remembers which Session Builder screen it
 * was opened from (its "parent"), keyed by React Router's per-entry key and
 * kept in sessionStorage so a reload doesn't forget it. Back steps down to
 * the parent — the same step the phone's back chevron and the browser's
 * Back button take — and only when there is no parent below (a link from an
 * email, a reload of a fresh tab) does it open the fallback screen in place
 * of this one. Either way the history never grows on the way back.
 */
const KEY = 'sa-parents'
const MAIN = '/assistant'
const GOTO = 'sa-goto'
const inBuilder = (path) => path === MAIN || path.startsWith(`${MAIN}/`)

function load() {
  try { return JSON.parse(sessionStorage.getItem(KEY) || '{}') || {} } catch { return {} }
}
function store(map) {
  const keys = Object.keys(map)
  for (const k of keys.slice(0, Math.max(0, keys.length - 300))) delete map[k]
  try { sessionStorage.setItem(KEY, JSON.stringify(map)) } catch { /* locked-down browser */ }
}

/** Mounted once, in the shell: records each Session Builder screen's parent as it opens. */
export function useBuilderHistory() {
  const location = useLocation()
  const type = useNavigationType()
  const prev = useRef(null)
  useEffect(() => {
    const before = prev.current
    prev.current = location
    if (!before || before.key === location.key || !inBuilder(location.pathname)) return
    const map = load()
    // Opened from another Session Builder screen: that screen is the parent.
    if (type === 'PUSH' && inBuilder(before.pathname)) map[location.key] = { key: before.key, path: before.pathname }
    // Swapped in place (the main screen's day/view in its address, the next
    // day of a run, "Book it and add a day"): same parent as the screen it replaced.
    else if (type === 'REPLACE' && map[before.key]) map[location.key] = map[before.key]
    else return
    store(map)
  }, [location, type])
}

/**
 * The main screen to land on after a pop, read once by the main screen:
 * { date, view, draft? } — "show me the day this booking was made for", or
 * from Find a time, the slot to open the booking card on.
 */
export function takeGoto() {
  try {
    const g = JSON.parse(sessionStorage.getItem(GOTO) || 'null')
    return g && Date.now() - g.at < 30_000 ? g : null
  } catch { return null }
}
export function clearGoto() {
  try { sessionStorage.removeItem(GOTO) } catch { /* locked-down browser */ }
}

const LABELS = { [MAIN]: 'Sessions', [`${MAIN}/billing`]: 'Billing', [`${MAIN}/calendars`]: 'Calendars', [`${MAIN}/find`]: 'Find a time' }

export function useBuilderBack() {
  const location = useLocation()
  const navigate = useNavigate()
  const map = load()
  const parent = map[location.key] || null
  const idx = () => window.history.state?.idx ?? 0
  return {
    /** Where Back goes, in words — "Sessions", "Billing", "Booking". */
    parentLabel: parent ? LABELS[parent.path] || (/^\/assistant\/\d+$/.test(parent.path) ? 'Booking' : 'Back') : null,
    /** One screen back to where this was opened from; else `fallback`, in place of this one. */
    back(fallback = MAIN) {
      if (parent && idx() > 0) navigate(-1)
      else navigate(fallback, { replace: true })
    },
    /**
     * Straight to the main screen however deep this is (a form opened from
     * a booking opened from the calendar), optionally on a given day/view.
     */
    toMain(goto) {
      let steps = 0
      let found = false
      for (let p = parent; p && steps < 20; p = map[p.key]) {
        steps++
        if (p.path === MAIN) { found = true; break }
      }
      // The day (and, from Find a time, the slot to open the card on) is
      // left for the main screen to read when it mounts.
      if (goto) {
        try { sessionStorage.setItem(GOTO, JSON.stringify({ ...goto, at: Date.now() })) } catch { /* locked-down browser */ }
      }
      if (found && idx() >= steps) {
        navigate(-steps)
        return
      }
      const q = new URLSearchParams()
      if (goto?.view) q.set('view', goto.view)
      if (goto?.date) q.set('date', goto.date)
      navigate(q.toString() ? `${MAIN}?${q}` : MAIN, { replace: true })
    },
  }
}
